import React, { useState, useEffect, useMemo } from 'react';
import { UserIcon, PlayIcon, HeartIcon, MagnifyingGlassIcon, CheckBadgeIcon, ShareIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import ApiService from '../services/api';

interface Musician {
  id: number;
  name: string;
  avatar?: string;
  genre: string;
  followers: number;
  works: number;
  rating: number;
  location?: string;
  description?: string;
  verified?: boolean;
  tags?: string[];
}

type SortOption = 'default' | 'name-asc' | 'name-desc' | 'followers-desc' | 'followers-asc' | 'works-desc' | 'works-asc';

const Musicians: React.FC = () => {
  const [musicians, setMusicians] = useState<Musician[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState<SortOption>('default');
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [selectedLetter, setSelectedLetter] = useState<string>('');
  const { t, language } = useLanguage();
  const navigate = useNavigate();

  // Translation helper function with fallbacks
  const getTranslation = (key: string, fallback: string) => {
    try {
      const translation = t(key);
      // If translation returns the key itself or is empty, use fallback
      return (translation === key || !translation) ? fallback : translation;
    } catch (error) {
      return fallback;
    }
  };

  const categories = useMemo(() => [
    { value: 'all', label: language === 'en' ? 'All' : '全部' },
    { value: 'singers', label: language === 'en' ? 'Singers' : '歌手' },
    { value: 'lyrists', label: language === 'en' ? 'Lyrists' : '作词人' },
    { value: 'composers', label: language === 'en' ? 'Composers' : '作曲家' },
    { value: 'producers', label: language === 'en' ? 'Producers' : '制作人' },
    { value: 'sound-engineers', label: language === 'en' ? 'Sound Engineers' : '音响工程师' },
    { value: 'video-producers', label: language === 'en' ? 'Video Producers' : '视频制作人' },
    { value: 'beat-boxers', label: language === 'en' ? 'Beat Boxers' : '节拍盒子' },
    { value: 'rappers', label: language === 'en' ? 'Rappers' : '说唱歌手' }
  ], [language]);

  const sortOptions = useMemo(() => [
    { 
      value: 'default', 
      label: language === 'en' ? 'Default' : '默认排序' 
    },
    { 
      value: 'name-asc', 
      label: language === 'en' ? 'Name (A-Z)' : '姓名 (A-Z)' 
    },
    { 
      value: 'name-desc', 
      label: language === 'en' ? 'Name (Z-A)' : '姓名 (Z-A)' 
    },
    { 
      value: 'followers-desc', 
      label: language === 'en' ? 'Most Popular' : '最受欢迎' 
    },
    { 
      value: 'followers-asc', 
      label: language === 'en' ? 'Least Popular' : '人气最少' 
    },
    { 
      value: 'works-desc', 
      label: language === 'en' ? 'Most Works' : '作品最多' 
    },
    { 
      value: 'works-asc', 
      label: language === 'en' ? 'Least Works' : '作品最少' 
    }
  ], [language]);

  // Generate alphabet letters for filtering
  const alphabetLetters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

  // Get available letters (letters that have musicians)
  const availableLetters = useMemo(() => {
    const letters = new Set<string>();
    musicians.forEach(musician => {
      if (musician.name) {
        letters.add(musician.name.charAt(0).toUpperCase());
      }
    });
    return letters;
  }, [musicians]);

  const mockMusicians = useMemo(() => [
    // Singers
    { 
      id: 1, 
      name: 'Emma Chen', 
      genre: 'singers', 
      followers: 22800, 
      works: 18, 
      rating: 4.2,
      location: 'Beijing',
      description: language === 'en' ? 'Pop vocalist with classical training' : '古典训练的流行歌手',
      verified: true,
      tags: ['Pop', 'Classical', 'Creative'],
      avatar: 'https://images.unsplash.com/photo-1494790108755-2616c3584643?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80'
    },
    { 
      id: 2, 
      name: 'Jay Wang', 
      genre: 'singers', 
      followers: 45900, 
      works: 15, 
      rating: 4.7,
      location: 'Guangzhou',
      description: language === 'en' ? 'Independent singer-songwriter' : '独立创作歌手',
      verified: true,
      tags: ['Pop', 'Independent', 'Creative'],
      avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80'
    },
    
    // Composers
    { 
      id: 3, 
      name: 'Alex Liu', 
      genre: 'composers', 
      followers: 25400, 
      works: 25, 
      rating: 4.4,
      location: 'Shanghai',
      description: language === 'en' ? 'Electronic music composer and arranger' : '电子音乐作曲家和编曲师',
      verified: true,
      tags: ['Electronic', 'Production', 'Arrangement'],
      avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80'
    },
    { 
      id: 4, 
      name: 'Sarah Kim', 
      genre: 'composers', 
      followers: 18700, 
      works: 31, 
      rating: 4.6,
      location: 'Seoul',
      description: language === 'en' ? 'Film score and orchestral composer' : '电影配乐和管弦乐作曲家',
      verified: true,
      tags: ['Orchestral', 'Film Score', 'Classical'],
      avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80'
    },
    
    // Lyrists
    { 
      id: 5, 
      name: 'Maya Zhang', 
      genre: 'lyrists', 
      followers: 18500, 
      works: 42, 
      rating: 4.9,
      location: 'Chengdu',
      description: language === 'en' ? 'Award-winning lyricist specializing in emotional storytelling' : '获奖作词人，专长情感故事叙述',
      verified: true,
      tags: ['Lyrics', 'Literature', 'Emotional'],
      avatar: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80'
    },
    { 
      id: 6, 
      name: 'David Chen', 
      genre: 'lyrists', 
      followers: 14200, 
      works: 38, 
      rating: 4.5,
      location: 'Taipei',
      description: language === 'en' ? 'Bilingual lyricist for pop and R&B' : '流行和R&B双语作词人',
      verified: false,
      tags: ['Pop Lyrics', 'R&B', 'Bilingual'],
      avatar: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80'
    },
    
    // Producers
    { 
      id: 7, 
      name: 'Mike Johnson', 
      genre: 'producers', 
      followers: 35600, 
      works: 67, 
      rating: 4.8,
      location: 'Los Angeles',
      description: language === 'en' ? 'Grammy-nominated hip-hop and pop producer' : '格莱美提名的嘻哈和流行音乐制作人',
      verified: true,
      tags: ['Hip-Hop', 'Pop Production', 'Mixing'],
      avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80'
    },
    { 
      id: 8, 
      name: 'Lisa Park', 
      genre: 'producers', 
      followers: 28900, 
      works: 45, 
      rating: 4.6,
      location: 'Tokyo',
      description: language === 'en' ? 'Electronic and EDM producer' : '电子和EDM制作人',
      verified: true,
      tags: ['EDM', 'Electronic', 'Synthesizer'],
      avatar: 'https://images.unsplash.com/photo-1494790108755-2616c3584643?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80'
    },
    
    // Sound Engineers
    { 
      id: 9, 
      name: 'Tom Wilson', 
      genre: 'sound-engineers', 
      followers: 12400, 
      works: 89, 
      rating: 4.7,
      location: 'Nashville',
      description: language === 'en' ? 'Professional mixing and mastering engineer' : '专业混音和母带工程师',
      verified: true,
      tags: ['Mixing', 'Mastering', 'Studio Engineering'],
      avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80'
    },
    
    // Video Producers
    { 
      id: 10, 
      name: 'Anna Rodriguez', 
      genre: 'video-producers', 
      followers: 16800, 
      works: 23, 
      rating: 4.5,
      location: 'Barcelona',
      description: language === 'en' ? 'Music video director and producer' : '音乐视频导演和制作人',
      verified: false,
      tags: ['Music Videos', 'Cinematography', 'Editing'],
      avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80'
    },
    
    // Beat Boxers
    { 
      id: 11, 
      name: 'Kevin Brown', 
      genre: 'beat-boxers', 
      followers: 8900, 
      works: 12, 
      rating: 4.3,
      location: 'New York',
      description: language === 'en' ? 'World championship beat boxer' : '世界冠军节拍盒子表演者',
      verified: false,
      tags: ['Beatboxing', 'Vocal Percussion', 'Hip-Hop'],
      avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80'
    },
    
    // Rappers
    { 
      id: 12, 
      name: 'MC Flow', 
      genre: 'rappers', 
      followers: 42300, 
      works: 28, 
      rating: 4.6,
      location: 'Atlanta',
      description: language === 'en' ? 'Underground rap artist and freestyle champion' : '地下说唱艺术家和自由风格冠军',
      verified: true,
      tags: ['Rap', 'Freestyle', 'Underground'],
      avatar: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80'
    },
    { 
      id: 13, 
      name: 'Ruby Li', 
      genre: 'rappers', 
      followers: 31700, 
      works: 19, 
      rating: 4.4,
      location: 'Hong Kong',
      description: language === 'en' ? 'Bilingual female rapper and songwriter' : '双语女性说唱歌手和作词人',
      verified: true,
      tags: ['Rap', 'Bilingual', 'Songwriter'],
      avatar: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80'
    }
  ], [language]);

  useEffect(() => {
    const fetchMusicians = async () => {
      setLoading(true);
      try {
        const data = await ApiService.getMusicians();
        // Filter out musicians with incomplete data
        const validMusicians = data.filter((musician: Musician) => 
          musician && 
          musician.id && 
          musician.name && 
          musician.genre && 
          typeof musician.followers === 'number' && 
          typeof musician.works === 'number' &&
          typeof musician.rating === 'number'
        );
        setMusicians(validMusicians);
      } catch (error) {
        console.error('Failed to fetch musicians:', error);
        setMusicians(mockMusicians);
      } finally {
        setLoading(false);
      }
    };

    fetchMusicians();
  }, [mockMusicians]);

  const filteredAndSortedMusicians = useMemo(() => {
    // First filter the musicians
    let filtered = musicians.filter(musician => {
      // Additional validation to ensure musician data is complete
      if (!musician || !musician.name || !musician.genre) return false;
      
      const matchesSearch = musician.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           musician.genre.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (musician.description && musician.description.toLowerCase().includes(searchTerm.toLowerCase()));
      const matchesCategory = selectedCategory === 'all' || musician.genre === selectedCategory;
      const matchesLetter = !selectedLetter || musician.name.charAt(0).toUpperCase() === selectedLetter;
      
      return matchesSearch && matchesCategory && matchesLetter;
    });

    // Then sort based on the selected option
    if (sortBy === 'name-asc') {
      filtered = [...filtered].sort((a, b) => a.name.localeCompare(b.name));
    } else if (sortBy === 'name-desc') {
      filtered = [...filtered].sort((a, b) => b.name.localeCompare(a.name));
    } else if (sortBy === 'followers-desc') {
      filtered = [...filtered].sort((a, b) => b.followers - a.followers);
    } else if (sortBy === 'followers-asc') {
      filtered = [...filtered].sort((a, b) => a.followers - b.followers);
    } else if (sortBy === 'works-desc') {
      filtered = [...filtered].sort((a, b) => b.works - a.works);
    } else if (sortBy === 'works-asc') {
      filtered = [...filtered].sort((a, b) => a.works - b.works);
    }
    // 'default' keeps original order

    return filtered;
  }, [musicians, searchTerm, selectedCategory, selectedLetter, sortBy]);

  // Format follower count
  const formatFollowers = (count: number) => {
    if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}k`;
    }
    return count.toString();
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.sort-dropdown')) {
        setShowSortDropdown(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">
            {language === 'en' ? 'Loading creators...' : '正在加载创作者...'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-4xl font-bold text-gray-900">
              {language === 'en' ? 'Creators' : '创作者'}
            </h1>
            
            {/* Alphabet Filter Button */}
            <div className="relative sort-dropdown">
              <button
                onClick={() => setShowSortDropdown(!showSortDropdown)}
                className="flex items-center space-x-2 px-6 py-3 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors shadow-lg"
              >
                <span className="text-sm font-medium">
                  {selectedLetter ? `${language === 'en' ? 'Letter' : '字母'}: ${selectedLetter}` : (language === 'en' ? 'Browse A-Z' : '字母浏览')}
                </span>
                <ChevronDownIcon className={`h-4 w-4 transition-transform ${showSortDropdown ? 'rotate-180' : ''}`} />
              </button>
              
              {/* Alphabet Popup */}
              {showSortDropdown && (
                <div className="absolute right-0 mt-2 w-96 bg-white border border-gray-200 rounded-2xl shadow-2xl z-50 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-gray-900">
                      {language === 'en' ? 'Browse & Sort' : '浏览和排序'}
                    </h3>
                    {selectedLetter && (
                      <button
                        onClick={() => {
                          setSelectedLetter('');
                          setShowSortDropdown(false);
                        }}
                        className="text-sm text-blue-600 hover:text-blue-700 font-medium px-3 py-1 rounded-md hover:bg-blue-50"
                      >
                        {language === 'en' ? 'Clear' : '清除'}
                      </button>
                    )}
                  </div>
                  
                  {/* Alphabet Filter */}
                  <div className="mb-6">
                    <h4 className="text-sm font-bold text-gray-900 mb-3">
                      {language === 'en' ? 'Browse by Name' : '按姓名浏览'}
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {alphabetLetters.map(letter => {
                        const hasMusicians = availableLetters.has(letter);
                        return (
                          <button
                            key={letter}
                            onClick={() => {
                              if (hasMusicians) {
                                setSelectedLetter(selectedLetter === letter ? '' : letter);
                                setShowSortDropdown(false);
                              }
                            }}
                            disabled={!hasMusicians}
                            className={`
                              w-10 h-10 rounded-lg text-sm font-bold transition-all duration-200 transform hover:scale-105
                              ${selectedLetter === letter 
                                ? 'bg-blue-600 text-white shadow-lg scale-105' 
                                : hasMusicians 
                                  ? 'bg-gray-50 text-gray-700 hover:bg-blue-50 hover:text-blue-600 hover:shadow-md border border-gray-200' 
                                  : 'bg-gray-100 text-gray-300 cursor-not-allowed border border-gray-200'
                              }
                            `}
                          >
                            {letter}
                          </button>
                        );
                      })}
                    </div>
                    
                    {selectedLetter && (
                      <div className="bg-blue-50 rounded-lg p-3 mt-3">
                        <div className="text-sm text-blue-700 font-medium">
                          {language === 'en' 
                            ? `Showing creators with names starting with "${selectedLetter}"` 
                            : `显示姓名以"${selectedLetter}"开头的创作者`
                          }
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Sort options */}
                  <div className="border-t border-gray-200 pt-4">
                    <h4 className="text-sm font-bold text-gray-900 mb-3">
                      {language === 'en' ? 'Sort Order' : '排序方式'}
                    </h4>
                    <div className="grid grid-cols-2 gap-2">
                      {sortOptions.map((option) => (
                        <button
                          key={option.value}
                          onClick={() => {
                            setSortBy(option.value as SortOption);
                          }}
                          className={`text-left px-3 py-2 text-sm rounded-lg transition-colors ${
                            sortBy === option.value ? 'bg-blue-50 text-blue-600 font-medium border border-blue-200' : 'text-gray-700 hover:bg-gray-50 border border-gray-200'
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          <p className="text-lg text-gray-600">
            {language === 'en' ? 'Discover talented creators for your music projects' : '发现有才华的创作者参与您的音乐项目'}
          </p>
        </div>

        <div className="flex gap-8">
          {/* Left Sidebar - Search and Categories */}
          <div className="w-80 flex-shrink-0">
            {/* Search */}
            <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder={language === 'en' ? 'Search creators...' : '搜索创作者...'}
                  className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            {/* Categories */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">
                {language === 'en' ? 'Categories' : '分类'}
              </h3>
              <div className="space-y-2">
                {categories.map(category => (
                  <button
                    key={category.value}
                    onClick={() => setSelectedCategory(category.value)}
                    className={`w-full text-left px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                      selectedCategory === category.value
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    {category.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Main Content - Musicians Grid */}
          <div className="flex-1">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredAndSortedMusicians.map((musician) => (
                <div 
                  key={musician.id} 
                  className="group bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 cursor-pointer flex flex-col"
                  onClick={() => navigate(`/musicians/${musician.id}`)}
                >
                  <div className="relative h-64 bg-gradient-to-br from-purple-400 to-pink-400 overflow-hidden">
                    {musician.avatar ? (
                      <img 
                        src={musician.avatar} 
                        alt={musician.name} 
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                        onError={(e) => {
                          // If image fails to load, hide it and show default icon
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <UserIcon className="h-20 w-20 text-white" />
                      </div>
                    )}
                    
                    {/* Tags */}
                    <div className="absolute top-4 left-4 flex flex-wrap gap-2">
                      <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                        musician.verified ? 'bg-blue-600 text-white' : 'bg-black bg-opacity-60 text-white'
                      }`}>
                        {categories.find(cat => cat.value === musician.genre)?.label || musician.genre}
                      </div>
                      {musician.verified && (
                        <div className="bg-green-500 text-white px-2 py-1 rounded-full text-xs font-medium flex items-center space-x-1">
                          <CheckBadgeIcon className="h-3 w-3" />
                          <span>{getTranslation('musician.detail.verified', language === 'en' ? 'Verified' : '认证')}</span>
                        </div>
                      )}
                    </div>

                    {/* Rating */}
                    <div className="absolute top-4 right-4 bg-black bg-opacity-60 backdrop-blur-sm rounded-full px-3 py-1 flex items-center space-x-1">
                      <StarIconSolid className="h-4 w-4 text-yellow-400" />
                      <span className="text-white text-sm font-medium">{musician.rating}</span>
                    </div>
                  </div>

                  <div className="p-6 flex flex-col flex-grow">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-gray-900 mb-1">{musician.name}</h3>
                        {musician.location && (
                          <p className="text-sm text-gray-500">{musician.location}</p>
                        )}
                      </div>
                      <button 
                        className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (navigator.share) {
                            navigator.share({
                              title: musician.name,
                              text: musician.description || `${getTranslation('musician.detail.about.artist', 'About')} ${musician.name}`,
                              url: window.location.href
                            });
                          } else {
                            // Fallback for browsers without Web Share API
                            navigator.clipboard.writeText(window.location.href);
                            alert(getTranslation('musician.detail.share', language === 'en' ? 'Link copied!' : '链接已复制！'));
                          }
                        }}
                        title={getTranslation('musician.detail.share', language === 'en' ? 'Share' : '分享')}
                      >
                        <ShareIcon className="h-5 w-5" />
                      </button>
                    </div>

                    {musician.description && (
                      <p className="text-gray-600 text-sm mb-4 line-clamp-2 leading-relaxed">
                        {musician.description}
                      </p>
                    )}

                    {/* Tags */}
                    {musician.tags && musician.tags.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-4">
                        {musician.tags.slice(0, 3).map((tag, index) => (
                          <span 
                            key={index}
                            className="px-2 py-1 bg-blue-50 text-blue-600 text-xs rounded-full font-medium"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Stats */}
                    <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center space-x-1">
                          <HeartIcon className="h-4 w-4" />
                          <span>
                            {formatFollowers(musician.followers)} {getTranslation('musicians.followers', language === 'en' ? 'followers' : '粉丝')}
                          </span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <PlayIcon className="h-4 w-4" />
                          <span>
                            {musician.works} {getTranslation('musicians.works', language === 'en' ? 'works' : '作品')}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-3 mt-auto">
                      <button 
                        className="flex-1 bg-blue-600 text-white py-3 px-4 rounded-full hover:bg-blue-700 transition-colors font-medium"
                        onClick={(e) => {
                          e.stopPropagation();
                          alert(`${language === 'en' ? 'Followed' : '已关注'} ${musician.name}！`);
                        }}
                      >
                        {language === 'en' ? 'Follow' : '关注'}
                      </button>
                      <button 
                        className="px-4 py-3 border border-gray-200 text-gray-600 rounded-full hover:bg-gray-50 transition-colors"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate('/chat');
                        }}
                      >
                        {language === 'en' ? 'Message' : '私信'}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              
              {filteredAndSortedMusicians.length === 0 && (
                <div className="col-span-full text-center py-16">
                  <div className="mx-auto h-24 w-24 text-gray-400 mb-4">
                    <UserIcon className="h-full w-full" />
                  </div>
                  <h3 className="text-xl font-medium text-gray-900 mb-2">
                    {getTranslation('musicians.no.results', language === 'en' ? 'No creators found' : '未找到创作者')}
                  </h3>
                  <p className="text-gray-500">
                    {getTranslation('musicians.no.results.desc', language === 'en' ? 'Try adjusting your search or category filter' : '请尝试调整搜索条件或分类筛选')}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Musicians;